import { ParagonFetchResult } from './paragonFetch';
import {
  BlobFileResponse,
  DownloadableFileResponse,
  EmailResponse,
  NoFileContentResponse,
  UrlFileResponse,
} from './responses';

export type Middleware<I, O> = (requestPromise: Promise<I>) => Promise<O>;

export function buildMiddleware<I, O>(dataModifier: (source: I) => O): Middleware<I, O> {
  return (requestPromise: Promise<I>) => requestPromise.then(dataModifier);
}

export function combineMiddleware<I, M, O>(first: Middleware<I, M>, second: Middleware<M, O>): Middleware<I, O> {
  return (requestPromise) => second(first(requestPromise));
}

export const returnResponseMiddleware: Middleware<ParagonFetchResult, unknown> = (requestPromise) =>
  requestPromise.then((result) => result.response);

export const errorHandlingMiddleware: Middleware<ParagonFetchResult, ParagonFetchResult> = (requestPromise) =>
  requestPromise.catch((error) => {
    if (error.response) {
      throw error.response;
    } else {
      throw error;
    }
  });

export const simpleRequestMiddleware = combineMiddleware(errorHandlingMiddleware, returnResponseMiddleware);

type PossibleFileResponse = ParagonFetchResult | NoFileContentResponse | DownloadableFileResponse;

export const blobFileMiddleware: Middleware<ParagonFetchResult, PossibleFileResponse> = (requestPromise) =>
  requestPromise.then((result) =>
    result.statusCode === 200
      ? new BlobFileResponse(result)
      : result.statusCode === 204
        ? new NoFileContentResponse()
        : result,
  );

export const urlFileMiddleware: Middleware<ParagonFetchResult, PossibleFileResponse> = (requestPromise) =>
  requestPromise.then((result) =>
    result.statusCode === 200
      ? new UrlFileResponse(result)
      : result.statusCode === 204
        ? new NoFileContentResponse()
        : result,
  );

export const emailFileMiddleware: Middleware<
  PossibleFileResponse,
  DownloadableFileResponse | NoFileContentResponse | EmailResponse
> = (requestPromise) =>
  requestPromise.then((result) => {
    if (
      result instanceof DownloadableFileResponse ||
      result instanceof NoFileContentResponse ||
      result.statusCode !== 202
    ) {
      return result;
    }

    return new EmailResponse();
  });

export const requireDownloadableFileMiddleware: Middleware<
  PossibleFileResponse,
  DownloadableFileResponse | NoFileContentResponse
> = (requestPromise) =>
  requestPromise.then((result) => {
    if (result instanceof DownloadableFileResponse || result instanceof NoFileContentResponse) {
      return result;
    }

    throw new Error('Downloadable file was not returned from server.');
  });
